import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { dateFormatter } from '../utils/date-format';
import { DatePipe } from '@angular/common';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'month-picker',
  template: `<div class="mb-4">
    <label [for]="controlName" class="block text-sm font-medium text-gray-700">
      {{ label }}
      <span *ngIf="isRequired() && label !== ''" class="text-red-500"> * </span>

      <mat-icon
        *ngIf="tooltipText"
        svgIcon="mat_outline:info"
        [matTooltip]="tooltipText"
        [matTooltipPosition]="tooltipPosition"
        [matTooltipShowDelay]="showDelay"
        [matTooltipHideDelay]="hideDelay"
        class="text-grey-500 cursor-pointer icon-size-5 relative top-[2px]"
      ></mat-icon>
    </label>
    <div
      class="flex h-9 w-full border border-gray-300 px-4 items-center rounded-md"
      [ngClass]="{ 'bg-gray-100': controlName.disabled }"
    >
      <input
        matInput
        [matDatepicker]="date"
        [placeholder]="placeholder"
        [id]="controlName"
        [name]="controlName"
        [formControl]="controlName"
        [matDatepickerFilter]="filters"
      />
      <mat-datepicker-toggle matSuffix [for]="date" class="ml-auto">
        <mat-icon
          svgIcon="feather:calendar"
          matDatepickerToggleIcon
          class="text-blue-500"
        ></mat-icon>
      </mat-datepicker-toggle>
      <mat-datepicker
        #date
        startView="year"
        (monthSelected)="setMonthAndYear($event, date)"
        (closed)="onDateSelected()"
      ></mat-datepicker>
    </div>
    <div
      class="invalid-feedback"
      *ngIf="controlName.invalid && controlName.touched"
    >
      <div
        *ngIf="controlName.errors.required"
        class="text-sm text-red-500 mt-1 ml-2"
      >
        This field is required.
      </div>
    </div>
  </div>`,
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class MonthPickerComponent implements OnInit {
  @Input() label: string = '';
  @Input() controlName: FormControl = new FormControl('');
  @Input() placeholder: string = 'MM/YYYY';
  @Input() alwaysFirstDayOfMonth: boolean = false;
  @Input() filters = (d: Date | null): boolean => true;
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Input() tooltipText: string = ''; // text shown in tooltip
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 50;
  @Input() hideDelay: number = 50;
  constructor(private datePipe: DatePipe) {}

  ngOnInit(): void {
    if (this.controlName.value) {
      this.onDateSelected();
    }
  }

  public onDateSelected(): void {
    this.controlName.setValue(
      dateFormatter(
        this.datePipe,
        this.controlName.value,
        !this.alwaysFirstDayOfMonth ? 'yyyy-MM-dd' : 'yyyy-MM-01'
      )
    );
    this.onSelect.emit(this.controlName.value);
  }

  public setMonthAndYear(normalizedMonthAndYear: any, datepicker: any): void {
    const ctrlValue = new Date();
    ctrlValue.setMonth(normalizedMonthAndYear.month());
    ctrlValue.setFullYear(normalizedMonthAndYear.year());

    this.controlName.setValue(ctrlValue);
    this.controlName.markAsDirty();
    this.controlName.updateValueAndValidity();

    datepicker.close();
  }

  public isRequired(): boolean {
    // check form if they have a required validation
    return this.controlName.hasValidator(Validators.required);
  }
}
